import styled, { useTheme } from 'styled-components';
import Modal from '../../../components/Modal';
import { BodyLarge, ParagraphLargeText } from '../../../components/Texts';
import Button from '../../../components/Button';
import { openCenteredWindow } from '../../../utils/window';
import { useState, useEffect } from 'react';
import TextInput from '../../../components/Inputs/Text';
import AmountInput from '../../../components/Inputs/Amount';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { usePrivy } from '@privy-io/react-auth';
import {
  useCreateKycLinkMutation,
  useGetKycStatusQuery,
  useCreateOnrampTransferMutation,
} from '../../../redux/api/authorized/bridge';
import { BRIDGE_TOS_CALLBACK } from '../../../components/BridgeCallback';
import { BRIDGE_KYC_CALLBACK } from '../../../components/BridgeKycCallback';
import LoadingSpinner from '../../../components/LoadingSpinner';
import { useDispatch } from 'react-redux';
import { openModal } from '../../../redux/slices/modals';
import { PHONE_MEDIA_QUERY, useMediaQuery } from '../../../common/responsive';

const ContentContainer = styled.div`
  width: 100%;
  overflow: hidden;
  @media ${PHONE_MEDIA_QUERY} {
    height: 100vh;
  }
`;

const StyledModal = styled(Modal)`
  .modal-content {
    overflow: hidden;
    max-height: 900px;
    overflow-y: auto;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 10px 0px;
`;

const ConfirmationContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 10px 0px;
`;

const ConfirmationRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 12px 0;
  border-bottom: 1px solid ${({ theme }) => theme.border.primary};
  gap: 16px;

  &:last-child {
    border-bottom: none;
  }
`;

const ConfirmationLabel = styled(BodyLarge)`
  color: ${({ theme }) => theme.text.medium};
  flex-shrink: 0;
`;

const ConfirmationValue = styled(BodyLarge)`
  font-weight: 500;
  word-break: break-all;
  text-align: right;
`;

const AddressContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Select = styled.select`
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.border.primary};
  border-radius: 4px;
  font-size: 14px;
  background: ${({ theme }) => theme.background.primary};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.primary.accent};
  }

  &.error {
    border-color: ${({ theme }) => theme.status.error.primary};
  }
`;

const ErrorText = styled.span`
  color: ${({ theme }) => theme.status.error.primary};
  font-size: 12px;
  margin-top: 4px;
`;

const StepIndicator = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 20px;
  @media ${PHONE_MEDIA_QUERY} {
    margin-bottom: 0px;
  }
`;

const StepDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${({ active, theme }) =>
    active ? `${theme.primary.accent}` : `${theme.border.secondary}`};
`;

const TosIframe = styled.iframe`
  width: 100%;
  height: 500px;
  border: 1px solid ${({ theme }) => theme.border.primary};
  border-radius: 8px;
  margin: 16px 0;
`;

const KycIframe = styled.iframe`
  width: 100%;
  height: 600px;
  margin: 16px 0;
  @media ${PHONE_MEDIA_QUERY} {
    margin: 8px 0;
    height: 80%;
  }
`;

const ConfirmationGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ConfirmationCard = styled.div`
  padding: 8px;
  border: 1px solid ${({ theme }) => theme.border.secondary};
  border-radius: 6px;
  background: ${({ theme }) => theme.background.light};
`;

const CompactConfirmationRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CompactConfirmationLabel = styled(BodyLarge)`
  color: ${({ theme }) => theme.text.medium};
  font-size: 12px;
  font-weight: 500;
`;

const CompactConfirmationValue = styled(BodyLarge)`
  font-weight: 600;
  word-break: break-all;
  font-size: 13px;
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const RadioOption = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.border.primary};
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;

  @media (hover: hover) {
    &:hover {
      border-color: ${({ theme }) => theme.primary.accent};
      background-color: ${({ theme }) => theme.primary.background};
    }
  }

  &.selected {
    border-color: ${({ theme }) => theme.primary.accent};
    background-color: ${({ theme }) => theme.primary.background};
  }

  &.error {
    border-color: ${({ theme }) => theme.status.error.primary};
  }
`;

const RadioInput = styled.input`
  margin: 0;
`;

const RadioLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
`;

const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.status.error.primary};
  font-size: 14px;
  text-align: center;
  margin-top: 12px;
  min-height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FieldError = styled.div`
  color: ${({ theme }) => theme.status.error.primary};
  font-size: 12px;
  margin-top: 4px;
  min-height: 16px;
  display: flex;
  align-items: center;
`;

const BridgeOnrampModal = ({ onClose }) => {
  const { user } = usePrivy();
  const [createKycLink] = useCreateKycLinkMutation();
  const [createOnrampTransfer] = useCreateOnrampTransferMutation();

  const [step, setStep] = useState(0); // Start at 0 for auto-check
  const [bridgeCustomerId, setBridgeCustomerId] = useState(null);
  const [transferData, setTransferData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tosIframeUrl, setTosIframeUrl] = useState(null);
  const [kycIframeUrl, setKycIframeUrl] = useState(null);
  const [error, setError] = useState(null);
  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);

  const theme = useTheme();
  const dispatch = useDispatch();

  // Get KYC status if user is authenticated
  const {
    data: kycStatus,
    error: kycStatusError,
    isLoading: kycStatusLoading,
  } = useGetKycStatusQuery(undefined, {
    pollingInterval: step === 1 ? 0 : 5000, // Only poll if not on email input step
    skip: !user || step === 1, // Skip if no user or on email input step
  });

  // Get user email from Privy
  const userEmail = user?.email?.address || user?.google?.email || user?.email;

  // Auto-determine flow based on KYC status
  useEffect(() => {
    if (step === 0 && user) {
      if (kycStatusError) {
        // KYC status query failed (likely 404 - no KYC link exists)
        if (userEmail) {
          // Create KYC link with user's email
          handleKycSubmit({ email: userEmail });
        } else {
          setStep(1);
        }
      } else if (!kycStatusLoading && kycStatus) {
        // Got KYC status, handle response
        handleKycResponse(kycStatus);
      } else if (!kycStatusLoading && !kycStatus) {
        // Query completed but returned no data, create new KYC link
        if (userEmail) {
          // Create KYC link with user's email
          handleKycSubmit({ email: userEmail });
        } else {
          setStep(1);
        }
      }
      // Still loading KYC status - wait
    }
  }, [step, user, kycStatus, kycStatusLoading, kycStatusError, userEmail]);

  // Step 1 - KYC Link Creation Schema (email only, name optional with new API)
  const kycSchema = yup.object({
    email: yup.string().email('Invalid email').required('Email is required'),
  });

  // Step 3 - Transfer Schema
  const transferSchema = yup.object({
    amount: yup
      .number()
      .min(10, 'Minimum amount is $10')
      .max(10000, 'Maximum amount is $10,000')
      .required('Amount is required')
      .typeError('Invalid amount'),
    payment_rail: yup.string().required('Payment method is required'),
  });

  const getCurrentSchema = () => {
    switch (step) {
      case 1:
        return kycSchema;
      case 4:
        return transferSchema;
      default:
        return yup.object({});
    }
  };

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
    trigger,
  } = useForm({
    mode: 'onBlur', // Validate when field loses focus
    reValidateMode: 'onBlur', // Re-validate on blur
    resolver: yupResolver(getCurrentSchema()),
    defaultValues: {
      email: '',
      amount: '',
      payment_rail: 'ach_push',
    },
  });

  useEffect(() => {
    if (kycStatus) {
      handleKycResponse(kycStatus);
    }
  }, [kycStatus]);

  useEffect(() => {
    // Listen for iframe callback messages
    const handleMessage = (event) => {
      if (event.data?.type === BRIDGE_TOS_CALLBACK) {
        if (event.data.success && event.data.signedAgreementId) {
          setLoading(false);
          // TOS completed, check if we need KYC verification
          if (kycStatus?.kyc_status === 'approved' && bridgeCustomerId) {
            setStep(4); // Go directly to transfers
          } else if (kycStatus?.kyc_link) {
            setupKycIframe(kycStatus.kyc_link);
            setStep(3); // Go to KYC iframe
          } else {
            setStep(4); // Fallback to transfers
          }
        } else {
          setLoading(false);
        }
      } else if (event.data?.type === BRIDGE_KYC_CALLBACK) {
        if (event.data.success && event.data.kycLinkId) {
          setLoading(false);
          setStep(4); // Go to transfers after KYC completion
        } else {
          // TODO: Should we go to step 6? What it means that success is false?
          setLoading(false);
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [kycStatus, bridgeCustomerId]);

  const setupTosIframe = (tosUrl) => {
    // Append redirect_uri as query parameter to the TOS URL
    const tosUrlWithRedirect = new URL(tosUrl);
    const redirectUri = `${window.location.origin}/bridge-tos-callback`;
    tosUrlWithRedirect.searchParams.append('redirect_uri', redirectUri);

    setTosIframeUrl(tosUrlWithRedirect.toString());
  };

  const setupKycIframe = (kycUrl) => {
    // Convert KYC link to iframe format
    // Replace /verify with /widget and add iframe-origin parameter
    const iframeUrl = new URL(kycUrl.replace('/verify', '/widget'));

    iframeUrl.searchParams.append('iframe-origin', window.location.origin);

    setKycIframeUrl(iframeUrl.toString());
  };

  const handleKycResponse = (response) => {
    // Set customer ID if available
    if (response.customer_id) {
      setBridgeCustomerId(response.customer_id);
    }

    // Check if user can create transfers (both KYC and TOS complete)
    if (response.canCreateTransfers) {
      setStep(4);
      setLoading(false);
      return;
    }

    // Check TOS status first - if pending, show TOS
    if (response.tos_status === 'pending' && response.tos_link) {
      setStep(2); // Step 2 is TOS step
      setupTosIframe(response.tos_link);
      setLoading(false);
      return;
    }

    // If TOS approved but KYC not complete, handle KYC verification
    if (response.kyc_status === 'approved') {
      // KYC already complete, go to transfers
      setStep(4);
      setLoading(false);
      return;
    }

    if (response.kyc_status === 'rejected') {
      dispatch(
        openModal({
          name: 'kycRejection',
        }),
      );
      setLoading(false);
      return;
    }

    // If KYC not started or incomplete, show KYC iframe
    if (
      ['not_started', 'incomplete'].includes(response.kyc_status) &&
      response.kyc_link
    ) {
      setupKycIframe(response.kyc_link);
      setStep(3); // Step 3 for KYC iframe (between TOS and transfers)
      setLoading(false);
    } else if (response.kyc_status === 'under_review') {
      setStep(6);
      setLoading(false);
    } else {
      setLoading(false);
    }
  };

  const handleKycSubmit = async (data) => {
    try {
      setLoading(true);
      setError(null);

      const response = await createKycLink({
        email: data.email,
      }).unwrap();

      handleKycResponse(response);
    } catch (error) {
      if (error.data?.code === 'duplicate_record') {
        // We have a duplicate record - use the existing_kyc_link if available
        if (error.data?.existing_kyc_link?.kyc_link) {
          setupKycIframe(error.data.existing_kyc_link.kyc_link);
          setStep(3);
          setLoading(false);
          return;
        }

        // If we have existing_kyc_link but no kyc_link, try to get status
        if (error.data?.existing_kyc_link) {
          try {
            const statusResponse = await useGetKycStatusQuery().unwrap();
            handleKycResponse(statusResponse);
            return;
          } catch (statusError) {
            setError('Failed to retrieve existing verification status');
          }
        } else {
          setError('Unable to retrieve existing verification link');
        }
      } else {
        setError(error.data?.message || 'Failed to start verification process');
      }
      setLoading(false);
    }
  };

  const handleTransferSubmit = async (data) => {
    try {
      setLoading(true);
      setError(null); // Clear any previous errors
      const transferPayload = {
        amount: data.amount.toString(),
        payment_rail: data.payment_rail,
        bridge_customer_id: bridgeCustomerId,
      };
      const response = await createOnrampTransfer(transferPayload).unwrap();
      setTransferData({
        ...data,
        transferId: response.id,
        source_deposit_instructions: response.source_deposit_instructions,
      });
      setStep(5);
    } catch (error) {
      setError(error.data?.message || 'Failed to create transfer');
    } finally {
      setLoading(false);
    }
  };

  // Only trigger validation when explicitly submitting
  const onSubmit = async (data) => {
    const isValid = await trigger();
    if (isValid) {
      if (step === 1) {
        handleKycSubmit(data);
      } else if (step === 2) {
        handleTosSubmit(data);
      } else if (step === 4) {
        handleTransferSubmit(data);
      }
    }
  };

  const handleStartOver = () => {
    setStep(1); // Start with name input
    setBridgeCustomerId(null);
    setTransferData(null);
    reset();
  };

  return (
    <StyledModal onClose={onClose}>
      <ContentContainer>
        {step > 0 && step <= 3 && (
          <StepIndicator>
            {[1, 2, 3].map((num) => (
              <StepDot
                key={num}
                active={
                  (step === 1 && num === 1) || // Email step
                  ((step === 2 || step === 3) && num === 2) || // TOS/KYC verification step
                  (step === 3 && num === 3) // KYC completion
                }
              />
            ))}
          </StepIndicator>
        )}

        {step === 6 && (
          <>
            <ParagraphLargeText>Verifying Your Identity</ParagraphLargeText>
            <BodyLarge>
              Your verification is in progress. This process typically takes
              between 1 minute and 1 day. We'll notify you as soon as it's
              complete!
            </BodyLarge>
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                margin: '20px 0',
              }}
            >
              <LoadingSpinner />
            </div>
          </>
        )}

        {step === 0 && (
          <>
            <ParagraphLargeText>Bridge Setup</ParagraphLargeText>
            <BodyLarge>
              {kycStatusLoading
                ? 'Checking verification status...'
                : 'Setting up your verification process...'}
            </BodyLarge>
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                margin: '20px 0',
              }}
            >
              <LoadingSpinner />
            </div>
          </>
        )}

        {step === 1 && (
          <>
            <ParagraphLargeText>Bridge Verification</ParagraphLargeText>
            <BodyLarge>
              Enter your email to start the verification process.
            </BodyLarge>

            <Form onSubmit={handleSubmit(onSubmit)}>
              <Controller
                name='email'
                control={control}
                render={({ field: { onChange, onBlur, value } }) => (
                  <TextInput
                    label='Email'
                    placeholder='Enter your email'
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    error={errors.email?.message}
                  />
                )}
              />

              <Button
                type='submit'
                fullWidth={true}
                disabled={!isValid || loading}
              >
                {loading
                  ? 'Creating verification link...'
                  : 'Start Verification'}
              </Button>
              {error && <ErrorMessage>{error}</ErrorMessage>}
              {errors.email && <FieldError>{errors.email.message}</FieldError>}
            </Form>
          </>
        )}

        {step === 2 && (
          <>
            <ParagraphLargeText>Accept Terms of Service</ParagraphLargeText>
            <BodyLarge>
              Please review and accept Bridge's Terms of Service to continue.
            </BodyLarge>

            {tosIframeUrl && (
              <TosIframe
                src={tosIframeUrl}
                title='Bridge Terms of Service'
                frameBorder='0'
              />
            )}

            <BodyLarge
              style={{
                textAlign: 'center',
                color: `${theme.text.medium}`,
                fontSize: '14px',
              }}
            >
              Complete the Terms of Service above to continue with verification.
            </BodyLarge>
          </>
        )}

        {step === 3 && (
          <>
            {!isMobile && (
              <>
                <ParagraphLargeText>
                  Complete Identity Verification
                </ParagraphLargeText>
                <BodyLarge>
                  Please complete your identity verification to continue.
                </BodyLarge>
              </>
            )}
            {kycIframeUrl && (
              <KycIframe
                src={kycIframeUrl}
                title='Bridge Identity Verification'
                frameBorder='0'
                allow='camera;'
                sandbox='allow-same-origin allow-scripts allow-forms allow-popups allow-modals allow-top-navigation-by-user-activation'
              />
            )}
            <BodyLarge
              style={{
                textAlign: 'center',
                color: `${theme.text.medium}`,
                fontSize: '14px',
              }}
            >
              Complete the identity verification above to continue.
            </BodyLarge>
          </>
        )}

        {step === 4 && (
          <>
            <ParagraphLargeText>Create Transfer</ParagraphLargeText>
            <BodyLarge>Set up your first onramp transfer.</BodyLarge>

            <Form onSubmit={handleSubmit(onSubmit)}>
              <Controller
                name='amount'
                control={control}
                render={({ field: { onChange, onBlur, value } }) => (
                  <AmountInput
                    label='Amount ($)'
                    placeholder='Enter amount'
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    error={errors.amount?.message}
                  />
                )}
              />

              <Controller
                name='payment_rail'
                control={control}
                render={({ field: { onChange, value } }) => (
                  <div>
                    <BodyLarge style={{ marginBottom: '8px' }}>
                      Payment Method
                    </BodyLarge>
                    <RadioGroup>
                      <RadioOption
                        className={value === 'ach_push' ? 'selected' : ''}
                      >
                        <RadioInput
                          type='radio'
                          name='payment_rail'
                          value='ach_push'
                          checked={value === 'ach_push'}
                          onChange={onChange}
                        />
                        <RadioLabel>ACH</RadioLabel>
                      </RadioOption>
                      <RadioOption
                        className={value === 'wire' ? 'selected' : ''}
                      >
                        <RadioInput
                          type='radio'
                          name='payment_rail'
                          value='wire'
                          checked={value === 'wire'}
                          onChange={onChange}
                        />
                        <RadioLabel>Wire Transfer</RadioLabel>
                      </RadioOption>
                    </RadioGroup>
                    {errors.payment_rail && (
                      <ErrorText>{errors.payment_rail.message}</ErrorText>
                    )}
                  </div>
                )}
              />

              <Button
                type='submit'
                fullWidth={true}
                disabled={!isValid || loading}
              >
                {loading ? 'Creating Transfer...' : 'Create Transfer'}
              </Button>
              {error && <ErrorMessage>{error}</ErrorMessage>}
              {errors.amount && (
                <FieldError>{errors.amount.message}</FieldError>
              )}
            </Form>
          </>
        )}

        {step === 5 && transferData && (
          <ConfirmationContainer>
            <ParagraphLargeText>Deposit Instructions</ParagraphLargeText>
            <BodyLarge>
              Complete your transfer by sending funds to the following bank
              account:
            </BodyLarge>

            <BodyLarge
              style={{
                marginTop: '16px',
                padding: '12px',
                backgroundColor: `${theme.primary.background}`,
                border: `1px solid ${theme.primary.accent}`,
                borderRadius: '8px',
                fontSize: '14px',
              }}
            >
              <strong>Important:</strong> Include the deposit message exactly as
              shown when making your transfer. This ensures your funds are
              properly credited to your account.
            </BodyLarge>

            {transferData.source_deposit_instructions && (
              <>
                <ConfirmationGrid>
                  <ConfirmationCard>
                    <CompactConfirmationRow>
                      <CompactConfirmationLabel>
                        Amount to Send
                      </CompactConfirmationLabel>
                      <CompactConfirmationValue>
                        ${transferData.source_deposit_instructions.amount}{' '}
                        {transferData.source_deposit_instructions.currency.toUpperCase()}
                      </CompactConfirmationValue>
                    </CompactConfirmationRow>
                  </ConfirmationCard>

                  <ConfirmationCard>
                    <CompactConfirmationRow>
                      <CompactConfirmationLabel>
                        Payment Method
                      </CompactConfirmationLabel>
                      <CompactConfirmationValue>
                        {transferData.source_deposit_instructions
                          .payment_rail === 'ach_push'
                          ? 'ACH Transfer'
                          : transferData.source_deposit_instructions
                                .payment_rail === 'wire'
                            ? 'Wire Transfer'
                            : transferData.source_deposit_instructions.payment_rail.toUpperCase()}
                      </CompactConfirmationValue>
                    </CompactConfirmationRow>
                  </ConfirmationCard>
                </ConfirmationGrid>

                <ConfirmationGrid>
                  <ConfirmationCard>
                    <CompactConfirmationRow>
                      <CompactConfirmationLabel>
                        Bank Name
                      </CompactConfirmationLabel>
                      <CompactConfirmationValue>
                        {transferData.source_deposit_instructions.bank_name}
                      </CompactConfirmationValue>
                    </CompactConfirmationRow>
                  </ConfirmationCard>

                  <ConfirmationCard>
                    <CompactConfirmationRow>
                      <CompactConfirmationLabel>
                        Bank Address
                      </CompactConfirmationLabel>
                      <CompactConfirmationValue>
                        {transferData.source_deposit_instructions.bank_address}
                      </CompactConfirmationValue>
                    </CompactConfirmationRow>
                  </ConfirmationCard>
                </ConfirmationGrid>

                <ConfirmationGrid>
                  <ConfirmationCard>
                    <CompactConfirmationRow>
                      <CompactConfirmationLabel>
                        Account Number
                      </CompactConfirmationLabel>
                      <CompactConfirmationValue>
                        {
                          transferData.source_deposit_instructions
                            .bank_account_number
                        }
                      </CompactConfirmationValue>
                    </CompactConfirmationRow>
                  </ConfirmationCard>

                  <ConfirmationCard>
                    <CompactConfirmationRow>
                      <CompactConfirmationLabel>
                        Routing Number
                      </CompactConfirmationLabel>
                      <CompactConfirmationValue>
                        {
                          transferData.source_deposit_instructions
                            .bank_routing_number
                        }
                      </CompactConfirmationValue>
                    </CompactConfirmationRow>
                  </ConfirmationCard>
                </ConfirmationGrid>

                <ConfirmationGrid>
                  <ConfirmationCard>
                    <CompactConfirmationRow>
                      <CompactConfirmationLabel>
                        Beneficiary Name
                      </CompactConfirmationLabel>
                      <CompactConfirmationValue>
                        {
                          transferData.source_deposit_instructions
                            .bank_beneficiary_name
                        }
                      </CompactConfirmationValue>
                    </CompactConfirmationRow>
                  </ConfirmationCard>

                  <ConfirmationCard>
                    <CompactConfirmationRow>
                      <CompactConfirmationLabel>
                        Beneficiary Address
                      </CompactConfirmationLabel>
                      <CompactConfirmationValue>
                        {
                          transferData.source_deposit_instructions
                            .bank_beneficiary_address
                        }
                      </CompactConfirmationValue>
                    </CompactConfirmationRow>
                  </ConfirmationCard>
                </ConfirmationGrid>

                <ConfirmationGrid>
                  <ConfirmationCard>
                    <CompactConfirmationRow>
                      <CompactConfirmationLabel>
                        Deposit Message
                      </CompactConfirmationLabel>
                      <CompactConfirmationValue
                        style={{ fontFamily: 'monospace', fontWeight: 'bold' }}
                      >
                        {
                          transferData.source_deposit_instructions
                            .deposit_message
                        }
                      </CompactConfirmationValue>
                    </CompactConfirmationRow>
                  </ConfirmationCard>
                </ConfirmationGrid>
              </>
            )}

            <ConfirmationRow>
              <ConfirmationLabel>Transfer ID</ConfirmationLabel>
              <ConfirmationValue>{transferData.transferId}</ConfirmationValue>
            </ConfirmationRow>

            <Button type='button' fullWidth={true} onClick={onClose}>
              Close
            </Button>

            <Button
              type='button'
              variant='secondary'
              fullWidth={true}
              onClick={handleStartOver}
            >
              Start New Transfer
            </Button>
          </ConfirmationContainer>
        )}
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 10,
            marginTop: 20,
          }}
        >
          Powered by
          <img src='/bridge-stripe.webp' style={{ width: 150 }} />
        </div>
      </ContentContainer>
    </StyledModal>
  );
};

export default BridgeOnrampModal;
